import {
  ACCOUNTING_METHODS,
  CUSTOM_REPORT,
  EXCEL_ONLY_REPORTS,
  FINANCIAL_STATEMENT_REPORTS,
  AGING_SUMMARY_REPORTS,
  REVENUE_RECOGNITION,
  ExportFormatType,
  EXPORT_FORMAT,
  AVAILABLE_REPORTS,
  TRIAL_BALANCE,
  INCOME_STATEMENT,
  REPORTS_WITH_GROUP_BY,
} from "constants/bookkeeping";
import { PERIOD } from "constants/revenueMetrics";
import { REVENUE_SCHEDULE_EXPORT_TYPES } from "constants/revenueScheduleExportTypes";
import {
  ReportItem,
  FinancialStatementReportItem,
  AgingSummaryReportItem,
  RevenueRecognitionReportItem,
  TrialBalanceReportItem,
  CustomReportItem,
} from "types/Models/consolidatedReport";

export const isFinancialStatementReport = (
  item: ReportItem
): item is FinancialStatementReportItem => {
  return FINANCIAL_STATEMENT_REPORTS.includes(
    item.value as (typeof FINANCIAL_STATEMENT_REPORTS)[number]
  );
};

export const isAgingSummaryReport = (
  item: ReportItem
): item is AgingSummaryReportItem => {
  return AGING_SUMMARY_REPORTS.includes(
    item.value as (typeof AGING_SUMMARY_REPORTS)[number]
  );
};

export const isRevenueRecognitionReport = (
  item: ReportItem
): item is RevenueRecognitionReportItem => {
  return item.value === REVENUE_RECOGNITION;
};

export const isTrialBalanceReport = (
  item: ReportItem
): item is TrialBalanceReportItem => {
  return item.value === TRIAL_BALANCE;
};

export const isCustomReport = (item: ReportItem): item is CustomReportItem => {
  return item.value === CUSTOM_REPORT;
};

export const isReportDisabled = (
  reportValue: string,
  exportFormat: ExportFormatType,
  accountingMethod: string
) => {
  if (
    EXCEL_ONLY_REPORTS.includes(
      reportValue as (typeof EXCEL_ONLY_REPORTS)[number]
    ) &&
    exportFormat !== EXPORT_FORMAT.EXCEL
  ) {
    return true;
  }

  if (
    reportValue === "REVENUE_RECOGNITION" &&
    accountingMethod !== ACCOUNTING_METHODS.ACCRUAL
  ) {
    return true;
  }

  return false;
};

export const getReportDisabledState = (
  reportValue: string,
  exportFormat: ExportFormatType,
  accountingMethod: string
) => {
  if (
    EXCEL_ONLY_REPORTS.includes(
      reportValue as (typeof EXCEL_ONLY_REPORTS)[number]
    ) &&
    exportFormat !== EXPORT_FORMAT.EXCEL
  ) {
    return { disabled: true, tooltip: "Available only in Excel" };
  }

  if (
    reportValue === "REVENUE_RECOGNITION" &&
    accountingMethod !== ACCOUNTING_METHODS.ACCRUAL
  ) {
    return { disabled: true, tooltip: "Available only in accrual method" };
  }

  return { disabled: false, tooltip: null };
};

export const removeDisabledReports = (
  contents: ReportItem[],
  exportFormat: ExportFormatType,
  accountingMethod: string
) => {
  return contents.filter((item) => {
    if (isCustomReport(item)) return true;

    const report = AVAILABLE_REPORTS.find((r) => r.label === item.name);
    if (!report) return false;
    return !isReportDisabled(report.value, exportFormat, accountingMethod);
  });
};

export const addDefaultGroupBy = (contents: ReportItem[]): ReportItem[] => {
  return contents.map((item) => {
    const needsGroupBy = REPORTS_WITH_GROUP_BY.includes(
      item.value as (typeof REPORTS_WITH_GROUP_BY)[number]
    );

    if (needsGroupBy && !("groupBy" in item)) {
      return { ...item, groupBy: "", groupByLabel: "None" };
    }

    return item;
  });
};

export const getUsedGroupBysForReport = (
  reportValue: string,
  selectedReports: ReportItem[]
): string[] => {
  return selectedReports
    .filter((item) => item.value === reportValue)
    .map((item) => {
      if ("classIds" in item && item.classIds?.length) return item.classIds[0];
      if ("groupBy" in item) return item.groupBy || "";
      return "";
    });
};

export const getGroupByOptionsForReport = (reportValue: string): string[] => {
  if (
    FINANCIAL_STATEMENT_REPORTS.includes(
      reportValue as (typeof FINANCIAL_STATEMENT_REPORTS)[number]
    )
  ) {
    return ["", PERIOD.MONTHLY, PERIOD.QUARTERLY, PERIOD.YEARLY];
  }

  if (reportValue === TRIAL_BALANCE) {
    return ["", PERIOD.MONTHLY];
  }

  if (reportValue === REVENUE_RECOGNITION) {
    return [
      REVENUE_SCHEDULE_EXPORT_TYPES.MONTHLY,
      REVENUE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS,
      REVENUE_SCHEDULE_EXPORT_TYPES.CUSTOMERS,
    ];
  }

  return [];
};

export const hasAvailableGroupByOptions = (
  reportValue: string,
  selectedReports: ReportItem[]
): boolean => {
  if (reportValue === INCOME_STATEMENT) return true;

  const usedGroupBys = getUsedGroupBysForReport(reportValue, selectedReports);
  const allOptions = getGroupByOptionsForReport(reportValue);

  return allOptions.some((opt) => !usedGroupBys.includes(opt));
};

const CUSTOM_REPORT_GROUP_BY_OPTIONS = [
  "",
  PERIOD.MONTHLY,
  PERIOD.QUARTERLY,
  PERIOD.YEARLY,
];

export const hasAvailableGroupByOptionsForCustomReport = (
  customReportId: string,
  selectedReports: ReportItem[]
): boolean => {
  const usedGroupBys = selectedReports
    .filter(
      (item): item is CustomReportItem =>
        isCustomReport(item) && item.custom_report_id === customReportId
    )
    .map((item) => item.groupBy ?? "");

  return CUSTOM_REPORT_GROUP_BY_OPTIONS.some(
    (opt) => !usedGroupBys.includes(opt)
  );
};
